import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Spinner, ListGroup, Badge } from 'react-bootstrap';
import toast from 'react-hot-toast';
import {
    getStationResponsibles,
    assignStationResponsible,
    removeStationResponsible
} from '../services/api';
import api from '../services/api';

/**
 * Modal for managing which users are responsible for a cash register station.
 * Used by Admin/Owner in the cash register settings page.
 *
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onHide - Callback to close the modal
 * @param {{ id, name }} station - The station being managed
 */
const StationResponsiblesModal = ({ show, onHide, station }) => {
    const [responsibles, setResponsibles] = useState([]);
    const [businessUsers, setBusinessUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [removingId, setRemovingId] = useState(null);

    const fetchData = useCallback(async () => {
        if (!station?.id) return;
        setLoading(true);
        try {
            const [responsiblesRes, usersRes] = await Promise.all([
                getStationResponsibles(station.id),
                api.get('/users')
            ]);
            setResponsibles(responsiblesRes.data);
            setBusinessUsers(usersRes.data);
        } catch (err) {
            console.error('Error fetching station data:', err);
            toast.error('Error al cargar los datos de la estación');
        } finally {
            setLoading(false);
        }
    }, [station?.id]);

    useEffect(() => {
        if (show) {
            fetchData();
            setSelectedUserId('');
        }
    }, [show, fetchData]);

    const handleAssign = async () => {
        if (!selectedUserId) return;
        setSaving(true);
        try {
            await assignStationResponsible(station.id, parseInt(selectedUserId));
            toast.success('Responsable asignado correctamente');
            setSelectedUserId('');
            fetchData();
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al asignar responsable';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (userId) => {
        setRemovingId(userId);
        try {
            await removeStationResponsible(station.id, userId);
            toast.success('Responsable eliminado');
            fetchData();
        } catch (err) {
            toast.error('Error al eliminar responsable');
        } finally {
            setRemovingId(null);
        }
    };

    // Filter out users already assigned
    const alreadyAssignedIds = new Set(responsibles.map(r => r.user_id));
    const assignableUsers = businessUsers.filter(u => !alreadyAssignedIds.has(u.id));

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    Responsables — <span className="text-primary">{station?.name}</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {loading ? (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" /> Cargando...
                    </div>
                ) : (
                    <>
                        {/* Current responsibles */}
                        <p className="text-muted small mb-2">
                            Solo los usuarios asignados podrán abrir turno en esta estación.
                            Los administradores y propietarios pueden abrirla siempre.
                        </p>

                        {responsibles.length === 0 ? (
                            <div className="text-muted text-center py-2 border rounded mb-3">
                                Sin responsables asignados
                            </div>
                        ) : (
                            <ListGroup className="mb-3">
                                {responsibles.map(r => (
                                    <ListGroup.Item
                                        key={r.user_id}
                                        className="d-flex justify-content-between align-items-center"
                                    >
                                        <div>
                                            <strong>{r.user_name}</strong>
                                            <span className="text-muted ms-2 small">{r.user_email}</span>
                                        </div>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            disabled={removingId === r.user_id}
                                            onClick={() => handleRemove(r.user_id)}
                                        >
                                            {removingId === r.user_id
                                                ? <Spinner animation="border" size="sm" />
                                                : '✕'}
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}

                        {/* Add new responsible */}
                        <div className="border rounded p-3">
                            <Form.Label className="fw-semibold">Agregar responsable</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Select
                                    value={selectedUserId}
                                    onChange={e => setSelectedUserId(e.target.value)}
                                    disabled={assignableUsers.length === 0}
                                >
                                    <option value="">
                                        {assignableUsers.length === 0
                                            ? 'Todos los usuarios ya están asignados'
                                            : '-- Seleccionar colaborador --'}
                                    </option>
                                    {assignableUsers.map(u => (
                                        <option key={u.id} value={u.id}>
                                            {u.name} ({u.email})
                                        </option>
                                    ))}
                                </Form.Select>
                                <Button
                                    variant="success"
                                    onClick={handleAssign}
                                    disabled={!selectedUserId || saving}
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    {saving ? <Spinner animation="border" size="sm" /> : 'Asignar'}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default StationResponsiblesModal;
